import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import {
  derivePageContext,
  describePageContext,
  pageContextKey,
} from '../../lib/pageContext';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Fab,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import ViewSidebarRoundedIcon from '@mui/icons-material/ViewSidebarRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import {
  dispatchLoanOpsApplyFilters,
  loanOpsChatApi,
  type LoanOpsChatSessionSummary,
} from '../../api/loanOpsChat';
import { GEMINI_GRADIENT } from '../../theme';
import { ChatMarkdown, hasChatVisual } from './chatMarkdown';
import { LoanOpsChatEmptyState } from './LoanOpsChatEmptyState';
import { LoanOpsChatHistory } from './LoanOpsChatHistory';
import {
  CHAT_HISTORY_WIDTH,
  CHAT_MAX_HEIGHT,
  CHAT_MAX_WIDTH,
  CHAT_MIN_HEIGHT,
  CHAT_MIN_WIDTH,
  chatBubbleExtrasFromUiActions,
  errorMessageFromChatFailure,
  formatChatTimestamp,
  loadChatPrefs,
  saveChatPrefs,
} from './loanOpsChatUi';

type ChatBubble = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  at: number;
  links?: Array<{ applicationId: string; label: string; href: string }>;
  /** Filters this turn pushed onto the Applications page, rendered so the jump is explainable. */
  filters?: string[];
  filterSearch?: string;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * One step above the theme's dialog radius — floating surfaces read softer than page cards.
 * Must stay a px string: `sx.borderRadius` is theme-aware and would multiply a bare number
 * by `theme.shape.borderRadius` (10), giving a 180px blob.
 */
const PANEL_RADIUS = '20px';

/**
 * Emotion `keyframes` rather than an inline `@keyframes` block in `sx` — the helper emits a
 * hashed, globally-unique name, so the rule can't be dropped or collide with another component.
 */
const typingBounce = keyframes({
  '0%, 80%, 100%': { opacity: 0.28, transform: 'translateY(0)' },
  '40%': { opacity: 1, transform: 'translateY(-3px)' },
});

/**
 * Reduced-motion variant. A progress indicator that freezes reads as "hung", so this still
 * animates — it just drops the vertical travel and fades instead.
 */
const typingPulse = keyframes({
  '0%, 80%, 100%': { opacity: 0.25 },
  '40%': { opacity: 1 },
});

const MotionBox = motion(Box);

export function LoanOpsChatPanel(): ReactNode {
  const theme = useTheme();
  const navigate = useNavigate();
  // Held in a ref so the send handler reads the CURRENT route without re-creating the
  // callback on every navigation — a stale closure here would report the page the panel
  // was opened on rather than the one the reviewer is asking from.
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  /*
   * Page context the reviewer has dismissed, held by KEY rather than a boolean.
   *
   * Keyed so the decision follows the record: dismissing it on one application keeps it
   * dismissed there, while navigating to a different one re-attaches automatically. A
   * boolean would leave them silently detached on a record they never dismissed, which is
   * the same invisible-influence problem the pill exists to solve, only inverted.
   */
  const [detachedKey, setDetachedKey] = useState<string | null>(null);
  const pageContext = useMemo(() => derivePageContext(location.pathname), [location.pathname]);
  const contextKey = pageContext ? pageContextKey(pageContext) : null;
  const contextAttached = Boolean(pageContext) && detachedKey !== contextKey;
  const isLight = theme.palette.mode === 'light';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const prefs = useMemo(() => loadChatPrefs(), []);
  const [open, setOpen] = useState(prefs.open);
  const [expanded, setExpanded] = useState(prefs.expanded);
  const [width, setWidth] = useState(prefs.width);
  const [height, setHeight] = useState(prefs.height);
  const [historyOpen, setHistoryOpen] = useState(prefs.historyOpen);

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [bubbles, setBubbles] = useState<ChatBubble[]>([]);
  const [sessions, setSessions] = useState<LoanOpsChatSessionSummary[]>([]);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pinned, setPinned] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /** Full-bleed = the panel owns the viewport (mobile always, desktop when expanded). */
  const fullBleed = isMobile || expanded;

  useEffect(() => {
    saveChatPrefs({ open, width, height, expanded, historyOpen });
  }, [open, width, height, expanded, historyOpen]);

  // Autoscroll only while the reviewer is pinned to the latest turn — never yank
  // their position mid-read.
  useEffect(() => {
    if (!open || !pinned) return;
    endRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [bubbles, busy, open, pinned, historyOpen, reduceMotion]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!copiedId) return;
    const t = window.setTimeout(() => setCopiedId(null), 1600);
    return () => window.clearTimeout(t);
  }, [copiedId]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setExpanded(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (expanded) setExpanded(false);
        else closePanel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, closePanel]);

  const refreshSessions = useCallback(async () => {
    try {
      const data = await loanOpsChatApi.listSessions();
      setSessions(data.sessions || []);
    } catch {
      /* ignore list failures — do not block chat */
    }
  }, []);

  useEffect(() => {
    if (open) void refreshSessions();
  }, [open, refreshSessions]);

  const startNewChat = useCallback(() => {
    setSessionId(undefined);
    setBubbles([]);
    setInput('');
    setBannerError(null);
    setPinned(true);
    inputRef.current?.focus();
  }, []);

  const send = useCallback(
    async (override?: string) => {
      const message = (override ?? input).trim();
      if (!message || busy) return;
      setInput('');
      setBannerError(null);
      setPinned(true);
      setBubbles((b) => [
        ...b,
        { id: `u-${Date.now()}`, role: 'user', text: message, at: Date.now() },
      ]);
      setBusy(true);
      try {
        // Read at send time, not render time: the reviewer may navigate between
        // opening the panel and hitting enter, and the page they are looking at when
        // they ask is the one they mean.
        const res = await loanOpsChatApi.chat({
          message,
          sessionId,
          // Read at send time so a mid-compose navigation is honoured, and skipped
          // entirely when the reviewer has detached this record.
          pageContext: contextAttached ? derivePageContext(locationRef.current) : null,
        });
        setSessionId(res.session_id);

        const ua = res.ui_actions ?? undefined;
        const extras = chatBubbleExtrasFromUiActions(ua);

        setBubbles((b) => [
          ...b,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            text: res.answer_text,
            at: Date.now(),
            ...extras,
          },
        ]);

        if (ua?.type === 'apply_filters' && ua.filters) {
          dispatchLoanOpsApplyFilters(ua.filters);
          navigate({
            pathname: '/applications',
            search: extras.filterSearch ?? '',
          });
        }
        void refreshSessions();
      } catch (err: unknown) {
        setBannerError(errorMessageFromChatFailure(err));
      } finally {
        setBusy(false);
      }
    },
    [input, busy, sessionId, navigate, refreshSessions],
  );

  const loadSession = useCallback(async (id: string) => {
    setBusy(true);
    setBannerError(null);
    setPinned(true);
    try {
      const s = await loanOpsChatApi.getSession(id);
      setSessionId(s.sessionId);
      setBubbles(
        (s.messages || []).map((msg) => {
          const isAssistant = msg.role === 'assistant';
          return {
            id: msg.id,
            role: isAssistant ? 'assistant' : 'user',
            text: msg.messageText,
            at: new Date(msg.createdAt).getTime() || Date.now(),
            // Rehydrate chips/links from the stored ui_actions so a reloaded
            // session looks identical to the turn as it was first answered.
            ...(isAssistant ? chatBubbleExtrasFromUiActions(msg.uiActions ?? undefined) : {}),
          };
        }),
      );
    } catch (err: unknown) {
      setBannerError(errorMessageFromChatFailure(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  }, []);

  const copyMessage = useCallback((b: ChatBubble) => {
    void navigator.clipboard?.writeText(b.text).then(
      () => setCopiedId(b.id),
      () => setBannerError('Could not copy to clipboard.'),
    );
  }, []);

  const onResizePointerDown = (e: ReactPointerEvent) => {
    if (fullBleed) return;
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY, w: width, h: height };
    const onMove = (ev: globalThis.PointerEvent) => {
      setWidth(clamp(start.w + (start.x - ev.clientX), CHAT_MIN_WIDTH, CHAT_MAX_WIDTH));
      setHeight(clamp(start.h + (start.y - ev.clientY), CHAT_MIN_HEIGHT, CHAT_MAX_HEIGHT));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const geometry = isMobile
    ? { inset: 0, width: 'auto', height: 'auto', borderRadius: 0, zIndex: theme.zIndex.modal + 1 }
    : expanded
      ? {
          top: 16,
          left: 16,
          right: 16,
          bottom: 16,
          width: 'auto',
          height: 'auto',
          borderRadius: PANEL_RADIUS,
          zIndex: theme.zIndex.modal + 1,
        }
      : {
          right: 24,
          bottom: 88,
          width,
          height,
          borderRadius: PANEL_RADIUS,
          zIndex: theme.zIndex.drawer + 1,
        };

  const transition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <>
      <Tooltip title={open ? 'Hide loan ops assistant' : 'Loan ops assistant'} placement="left">
        <Fab
          size="medium"
          onClick={() => (open ? closePanel() : setOpen(true))}
          aria-label={open ? 'Hide loan ops assistant' : 'Open loan ops assistant'}
          aria-expanded={open}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            color: '#fff',
            background: GEMINI_GRADIENT,
            boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, isLight ? 0.35 : 0.55)}`,
            zIndex: theme.zIndex.modal + 2,
            transition: 'transform .18s ease, box-shadow .18s ease',
            '&:hover': {
              background: GEMINI_GRADIENT,
              transform: 'translateY(-2px)',
              boxShadow: `0 14px 34px ${alpha(theme.palette.primary.main, isLight ? 0.45 : 0.65)}`,
            },
            display: open && fullBleed ? 'none' : 'flex',
          }}
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!bannerError || open}
            overlap="circular"
            aria-hidden
          >
            {open ? <CloseRoundedIcon /> : <AutoAwesomeRoundedIcon />}
          </Badge>
        </Fab>
      </Tooltip>

      <AnimatePresence>
        {open && (
          <MotionBox
            key="loan-ops-chat-panel"
            role="dialog"
            aria-label="Loan ops assistant"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 12, scale: reduceMotion ? 1 : 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : 8, scale: reduceMotion ? 1 : 0.99 }}
            transition={transition}
            sx={{
              position: 'fixed',
              ...geometry,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: isMobile ? 0 : '1px solid',
              borderColor: alpha(theme.palette.primary.main, isLight ? 0.22 : 0.38),
              boxShadow: isLight
                ? `0 24px 64px ${alpha(theme.palette.common.black, 0.18)}`
                : `0 24px 64px ${alpha(theme.palette.common.black, 0.6)}`,
            }}
          >
            {!fullBleed && (
              <Box
                onPointerDown={onResizePointerDown}
                role="separator"
                aria-label="Resize chat panel"
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 18,
                  height: 18,
                  cursor: 'nwse-resize',
                  zIndex: 3,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 6,
                    top: 6,
                    width: 7,
                    height: 7,
                    borderTop: '2px solid',
                    borderLeft: '2px solid',
                    borderColor: alpha(theme.palette.text.primary, 0.28),
                    borderTopLeftRadius: '2px',
                  },
                }}
              />
            )}

            {/* ── Header ─────────────────────────────────────────────── */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.25,
                py: 1,
                pl: fullBleed ? 1.25 : 2,
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                background: isLight
                  ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${alpha(theme.palette.background.paper, 1)} 62%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 1)} 62%)`,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '9px',
                  background: GEMINI_GRADIENT,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeRoundedIcon sx={{ fontSize: '1rem' }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }} noWrap>
                  Loan ops assistant
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.6}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: busy ? 'warning.main' : 'success.main',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
                    {busy ? 'Working…' : bubbles.length ? 'Ready' : 'Ask about applications'}
                  </Typography>
                </Stack>
              </Box>

              <Tooltip title={historyOpen ? 'Hide history' : 'Show history'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setHistoryOpen((v) => !v);
                    if (!historyOpen) {
                      void refreshSessions();
                      // Docked panel: widen so the transcript keeps a readable column.
                      if (!fullBleed) {
                        setWidth((w) =>
                          clamp(
                            Math.max(w, CHAT_MIN_WIDTH + CHAT_HISTORY_WIDTH),
                            CHAT_MIN_WIDTH,
                            CHAT_MAX_WIDTH,
                          ),
                        );
                      }
                    }
                  }}
                  aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
                  aria-pressed={historyOpen}
                  color={historyOpen ? 'primary' : 'default'}
                >
                  <ViewSidebarRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="New chat">
                <IconButton size="small" onClick={startNewChat} aria-label="Start a new chat">
                  <AddCommentRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!isMobile && (
                <Tooltip title={expanded ? 'Exit full screen' : 'Expand'}>
                  <IconButton
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    aria-label={expanded ? 'Exit full screen' : 'Expand to full screen'}
                  >
                    {expanded ? (
                      <CloseFullscreenRoundedIcon fontSize="small" />
                    ) : (
                      <OpenInFullRoundedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close">
                <IconButton size="small" onClick={closePanel} aria-label="Close assistant">
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {bannerError && (
              <Alert
                severity="error"
                onClose={() => setBannerError(null)}
                sx={{
                  borderRadius: 0,
                  py: 0.35,
                  flexShrink: 0,
                  '& .MuiAlert-message': { fontSize: '0.78rem', lineHeight: 1.45 },
                }}
              >
                {bannerError}
              </Alert>
            )}

            {/* ── Body ───────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              {historyOpen && (
                <LoanOpsChatHistory
                  sessions={sessions}
                  activeSessionId={sessionId}
                  onSelect={(id) => void loadSession(id)}
                  onNewChat={startNewChat}
                />
              )}

              <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
                <Box
                  ref={scrollRef}
                  onScroll={onScroll}
                  role="log"
                  aria-live="polite"
                  aria-relevant="additions text"
                  aria-busy={busy}
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: fullBleed ? 3 : 1.75,
                    py: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ maxWidth: fullBleed ? 820 : '100%', mx: 'auto' }}>
                    {bubbles.length === 0 && !busy && (
                      <LoanOpsChatEmptyState
                        onPick={(p) => void send(p)}
                        compact={!fullBleed && height < 520}
                      />
                    )}

                    {bubbles.map((b) => (
                      <ChatMessage
                        key={b.id}
                        bubble={b}
                        fullBleed={fullBleed}
                        copied={copiedId === b.id}
                        onCopy={() => copyMessage(b)}
                        reduceMotion={reduceMotion}
                      />
                    ))}

                    {busy && <TypingIndicator />}
                    <div ref={endRef} />
                  </Box>
                </Box>

                {!pinned && (
                  <Tooltip title="Jump to latest">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPinned(true);
                        endRef.current?.scrollIntoView({
                          behavior: reduceMotion ? 'auto' : 'smooth',
                        });
                      }}
                      aria-label="Jump to latest message"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 3,
                        '&:hover': { bgcolor: 'background.paper' },
                      }}
                    >
                      <KeyboardArrowDownRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* ── Composer ───────────────────────────────────────────── */}
            <Divider />
            <Box sx={{ px: fullBleed ? 3 : 1.5, pt: 1.25, pb: 1, flexShrink: 0 }}>
              <Box sx={{ maxWidth: fullBleed ? 820 : '100%', mx: 'auto' }}>
                {/*
                  What the assistant can see, made visible.
                  Page context silently changes what "this one" resolves to. Without a pill
                  a reviewer cannot tell why the answer knew which application they meant,
                  and cannot ask a general question from a detail page. Showing it costs one
                  line; leaving it hidden costs them the ability to reason about the answer.
                */}
                {pageContext && contextAttached && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    role="group"
                    aria-label="Page context attached to your next message"
                    sx={{
                      mb: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.06 : 0.15),
                      width: 'fit-content',
                      maxWidth: '100%',
                    }}
                  >
                    <MyLocationRoundedIcon
                      sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }}
                      aria-hidden
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                      Looking at
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {describePageContext(pageContext)}
                    </Typography>
                    <Tooltip title="Detach page context">
                      <IconButton
                        size="small"
                        onClick={() => setDetachedKey(contextKey)}
                        aria-label={`Stop sending ${describePageContext(pageContext)} with your messages`}
                        sx={{ p: 0.25, ml: 0.25 }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}

                {/* Detaching must not be a one-way door. */}
                {pageContext && !contextAttached && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MyLocationRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setDetachedKey(null)}
                    sx={{ mb: 1, py: 0.25, borderRadius: 2, textTransform: 'none' }}
                  >
                    Attach this page
                  </Button>
                )}

                <Stack direction="row" alignItems="flex-end" spacing={1}>
                  <TextField
                    inputRef={inputRef}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={5}
                    placeholder="Ask about applications…"
                    value={input}
                    disabled={busy}
                    onChange={(e) => setInput(e.target.value)}
                    inputProps={{ 'aria-label': 'Message the loan ops assistant' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        py: 0.85,
                        fontSize: '0.85rem',
                        bgcolor: alpha(theme.palette.text.primary, isLight ? 0.02 : 0.06),
                      },
                    }}
                  />
                  <Tooltip title="Send">
                    <span>
                      <IconButton
                        onClick={() => void send()}
                        disabled={busy || !input.trim()}
                        aria-label="Send message"
                        sx={{
                          width: 38,
                          height: 38,
                          color: '#fff',
                          background: GEMINI_GRADIENT,
                          '&:hover': { background: GEMINI_GRADIENT, filter: 'brightness(1.08)' },
                          '&.Mui-disabled': {
                            background: alpha(theme.palette.text.primary, isLight ? 0.1 : 0.16),
                            color: theme.palette.text.disabled,
                          },
                        }}
                      >
                        <SendRoundedIcon sx={{ fontSize: '1.05rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.6, px: 0.5, fontSize: '0.66rem' }}
                >
                  Enter to send · Shift + Enter for a new line
                </Typography>
              </Box>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
}

function ChatMessage({
  bubble,
  fullBleed,
  copied,
  onCopy,
  reduceMotion,
}: {
  bubble: ChatBubble;
  fullBleed: boolean;
  copied: boolean;
  onCopy: () => void;
  reduceMotion: boolean;
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const isUser = bubble.role === 'user';
  // A turn carrying a chart, KPI row or table needs the full column; a prose turn
  // still reads better inset. Widen that bubble only.
  const wide = !isUser && hasChatVisual(bubble.text);

  return (
    <MotionBox
      initial={{ opacity: 0, y: reduceMotion ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        display: 'flex',
        gap: 1,
        mb: 1.5,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        '&:hover .chat-msg-actions': { opacity: 1 },
      }}
    >
      {!isUser && (
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            mt: 0.25,
            borderRadius: '8px',
            bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.1 : 0.22),
            border: (t) => `1px solid ${alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.18 : 0.32)}`,
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: '0.85rem' }} />
        </Box>
      )}

      <Box sx={{ maxWidth: wide ? '100%' : fullBleed ? '82%' : '88%', minWidth: 0, flex: wide ? 1 : 'initial' }}>
        <Box
          sx={{
            px: 1.5,
            py: 1.1,
            borderRadius: 2,
            border: '1px solid',
            ...(isUser
              ? {
                  bgcolor: alpha(theme.palette.primary.main, isLight ? 0.1 : 0.24),
                  borderColor: alpha(theme.palette.primary.main, isLight ? 0.2 : 0.38),
                  borderBottomRightRadius: '6px', // longhand corners are raw px, not theme-scaled
                }
              : {
                  bgcolor: alpha(theme.palette.text.primary, isLight ? 0.025 : 0.07),
                  borderColor: 'divider',
                  borderBottomLeftRadius: '6px', // longhand corners are raw px, not theme-scaled
                }),
          }}
        >
          <Typography
            variant="body2"
            component="div"
            // Only the user turn is raw text needing `pre-wrap`; the markdown renderer
            // owns whitespace per block, and pre-wrap here would stretch table cells.
            sx={{
              wordBreak: 'break-word',
              lineHeight: 1.55,
              ...(isUser ? { whiteSpace: 'pre-wrap' } : null),
            }}
          >
            {isUser ? bubble.text : <ChatMarkdown content={bubble.text} />}
          </Typography>

          {bubble.filters && bubble.filters.length > 0 && (
            <Box
              sx={{
                mt: 1.15,
                pt: 1,
                borderTop: '1px dashed',
                borderColor: alpha(theme.palette.primary.main, isLight ? 0.3 : 0.45),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.75 }}>
                <FilterAltRoundedIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.68rem' }}
                >
                  Applied on the Applications page
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {bubble.filters.map((f) => (
                  <Chip
                    key={f}
                    size="small"
                    label={f}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.68rem', borderColor: 'divider' }}
                  />
                ))}
              </Stack>
              <Button
                size="small"
                component={RouterLink}
                to={{ pathname: '/applications', search: bubble.filterSearch || '' }}
                startIcon={<OpenInNewRoundedIcon sx={{ fontSize: '0.85rem' }} />}
                sx={{ mt: 0.75, px: 1, minHeight: 26, fontSize: '0.72rem' }}
              >
                Open results
              </Button>
            </Box>
          )}

          {bubble.links && bubble.links.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.15 }}>
              {bubble.links.map((l) => (
                <Chip
                  key={l.applicationId}
                  size="small"
                  icon={<OpenInNewRoundedIcon />}
                  label={l.label}
                  component={RouterLink}
                  to={l.href.startsWith('/') ? l.href : `/applications/${l.applicationId}`}
                  clickable
                  variant="outlined"
                  sx={{ maxWidth: 1, fontSize: '0.72rem' }}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ mt: 0.35, px: 0.5, justifyContent: isUser ? 'flex-end' : 'flex-start' }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem' }}>
            {formatChatTimestamp(bubble.at)}
          </Typography>
          <Tooltip title={copied ? 'Copied' : 'Copy message'}>
            <IconButton
              className="chat-msg-actions"
              size="small"
              onClick={onCopy}
              aria-label="Copy message text"
              sx={{
                p: 0.25,
                opacity: 0,
                transition: 'opacity .15s ease',
                '&:focus-visible': { opacity: 1 },
              }}
            >
              {copied ? (
                <CheckRoundedIcon sx={{ fontSize: '0.8rem', color: 'success.main' }} />
              ) : (
                <ContentCopyRoundedIcon sx={{ fontSize: '0.8rem' }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </MotionBox>
  );
}

function TypingIndicator() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 26,
          height: 26,
          mt: 0.25,
          borderRadius: '8px',
          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.1 : 0.22),
          border: (t) => `1px solid ${alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.18 : 0.32)}`,
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: '0.85rem' }} />
      </Box>
      <Box
        aria-label="Assistant is thinking"
        sx={{
          px: 1.6,
          py: 1.2,
          borderRadius: 2,
          borderBottomLeftRadius: 6,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, isLight ? 0.025 : 0.07),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          '& > span': {
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, isLight ? 0.75 : 0.9),
            display: 'inline-block',
            animation: `${typingBounce} 1.2s ease-in-out infinite`,
          },
          '& > span:nth-of-type(2)': { animationDelay: '0.15s' },
          '& > span:nth-of-type(3)': { animationDelay: '0.3s' },
          '@media (prefers-reduced-motion: reduce)': {
            '& > span': { animation: `${typingPulse} 1.6s ease-in-out infinite` },
          },
        }}
      >
        <Box component="span" />
        <Box component="span" />
        <Box component="span" />
      </Box>
    </Box>
  );
}
